import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge, { formatCurrency } from "@/components/StatusBadge";
import { useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { FileText, RotateCcw, Search, Layers, Link2, Sparkles, RotateCcw as RotateIcon, ShieldCheck } from "lucide-react";
import { useTableSort } from "@/lib/useTableSort";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function PendingInvoices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  // creating can be a single row { stage, project, client } OR a consolidated payload
  // { consolidated: true, rows: [...], client, project, currency }
  const [creating, setCreating] = useState(null);
  const [form, setForm] = useState({ invoice_number: "", invoice_date: new Date().toISOString().slice(0, 10), due_date: "", tax_amount: 0, notes: "" });

  // Multi-select for consolidated invoice creation
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Over-invoice 409 dialog state — shows per-project headroom
  const [blockWarning, setBlockWarning] = useState(null);

  // "Link existing invoice" dialog — retroactively assign a stage to an
  // invoice that was already created via the Invoices page.
  const [linking, setLinking] = useState(null); // { stage, project, client }
  const [linkInvoices, setLinkInvoices] = useState([]);   // pool for this project
  const [linkMode, setLinkMode] = useState("pick");       // "pick" | "manual"
  const [linkInvoiceId, setLinkInvoiceId] = useState("");
  const [linkInvoiceNumber, setLinkInvoiceNumber] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);

  // "Audit / Undo" panel state — shows recently linked stages so an admin can
  // un-link if the wrong invoice was picked.
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditBusyId, setAuditBusyId] = useState(null);
  const [auditWindow, setAuditWindow] = useState(30);

  // One-click accept for a smart auto-suggestion — no dialog, just link and refresh
  const [suggestBusyStageId, setSuggestBusyStageId] = useState(null);
  const acceptSuggestion = async (stage, invoice) => {
    if (!window.confirm(`Link stage "${stage.stage_name}" to invoice ${invoice.invoice_number}?`)) return;
    setSuggestBusyStageId(stage.id);
    try {
      await api.post(`/api/payment-stages/${stage.id}/link-invoice`, { invoice_id: invoice.id });
      toast.success(`Stage linked to ${invoice.invoice_number}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link");
    } finally { setSuggestBusyStageId(null); }
  };

  const loadAudit = async () => {
    try {
      const { data } = await api.get(`/api/payment-stages/recently-linked?days=${auditWindow}`);
      setAuditEvents(data?.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    }
  };
  const openAudit = async () => { setAuditOpen(true); await loadAudit(); };

  // Bulk-accept every suggested match on the current filtered list — one
  // click clears rows that have an unambiguous suggestion.
  const [bulkAcceptBusy, setBulkAcceptBusy] = useState(false);
  const bulkAcceptable = useMemo(() => (
    items.filter((it) => Array.isArray(it.suggested_invoices) && it.suggested_invoices.length > 0)
  ), [items]);
  const acceptAllSuggested = async () => {
    if (bulkAcceptable.length === 0) return;
    if (!window.confirm(`Accept ${bulkAcceptable.length} suggested match${bulkAcceptable.length === 1 ? "" : "es"}?\n\nEach stage will be linked to its highlighted invoice.`)) return;
    setBulkAcceptBusy(true);
    try {
      const body = {
        items: bulkAcceptable.map((it) => ({
          stage_id: it.stage.id,
          invoice_id: it.suggested_invoices[0].id,
        })),
      };
      const { data } = await api.post("/api/payment-stages/bulk-link-invoices", body);
      if (data.linked_count > 0 && data.skipped_count === 0) {
        toast.success(`${data.linked_count} stage${data.linked_count === 1 ? "" : "s"} linked`);
      } else if (data.linked_count > 0) {
        toast.success(`${data.linked_count} linked · ${data.skipped_count} skipped`);
      } else {
        toast.error(`0 linked · ${data.skipped_count} skipped (${data.skipped?.[0]?.reason || "check details"})`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk-link failed");
    } finally { setBulkAcceptBusy(false); }
  };
  const unlinkStage = async (ev) => {
    if (!window.confirm(`Un-link stage "${ev.stage.stage_name}" from ${ev.invoice_number}? It will return to the Pending Invoices list.`)) return;
    setAuditBusyId(ev.stage.id);
    try {
      await api.post(`/api/payment-stages/${ev.stage.id}/unlink-invoice`);
      toast.success("Un-linked · stage restored to pending");
      await loadAudit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to un-link");
    } finally { setAuditBusyId(null); }
  };

  // Filters (partial match)
  const [projectQ, setProjectQ] = useState("");
  const [clientQ, setClientQ] = useState("");
  const [bdQ, setBdQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/api/payment-stages/pending");
    setItems(data);
    setSelectedIds(new Set());
  };
  useEffect(() => { load(); }, []);

  const openCreate = (it) => {
    setCreating(it);
    const today = new Date();
    const due = new Date(today.getTime() + 30 * 86400000);
    setForm({
      invoice_number: "",
      invoice_date: today.toISOString().slice(0, 10),
      due_date: due.toISOString().slice(0, 10),
      tax_amount: 0,
      notes: `Stage ${it.stage.stage_number} · ${it.stage.stage_name}`,
    });
  };

  const openConsolidated = () => {
    const rows = items.filter((it) => selectedIds.has(it.stage.id));
    if (rows.length < 2) { toast.error("Select at least two pending stages to consolidate."); return; }
    const first = rows[0];
    const clientId = first.project.client_id;
    const currency = (first.stage.currency || "USD").toUpperCase();
    const mismatch = rows.find((r) => r.project.client_id !== clientId || (r.stage.currency || "USD").toUpperCase() !== currency);
    if (mismatch) { toast.error("All selected stages must belong to the same client and currency."); return; }
    const today = new Date();
    const due = new Date(today.getTime() + 30 * 86400000);
    setCreating({ consolidated: true, rows, client: first.client, project: first.project, currency });
    setForm({
      invoice_number: "",
      invoice_date: today.toISOString().slice(0, 10),
      due_date: due.toISOString().slice(0, 10),
      tax_amount: 0,
      notes: `Consolidated invoice covering ${rows.length} project stages`,
    });
  };

  // Auto-prefill invoice_number from the entity's active series so the
  // accountant never has to type/guess (same UX as the Invoices page).
  useEffect(() => {
    if (!creating) return;
    if (form.invoice_number) return; // don't clobber a user edit
    const currency = (creating.consolidated ? creating.currency : creating.stage.currency) || "USD";
    const entity = currency.toUpperCase() === "INR" ? "india" : "llc";
    let cancelled = false;
    api.get(`/api/invoice-series/next-number?entity=${entity}&date=${form.invoice_date}`)
      .then((r) => {
        if (cancelled) return;
        setForm((prev) => prev.invoice_number ? prev : { ...prev, invoice_number: r.data.invoice_number });
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating, form.invoice_date]);

  const saveInvoice = async () => {
    try {
      if (creating.consolidated) {
        const rows = creating.rows;
        const currency = creating.currency;
        const entity = currency.toUpperCase() === "INR" ? "india" : "llc";
        const line_items = rows.map((r) => ({
          description: `${r.project.project_code || ""} — Stage ${r.stage.stage_number}: ${r.stage.stage_name}`.trim(),
          quantity: 1,
          rate: Number(r.stage.expected_amount || 0),
          amount: Number(r.stage.expected_amount || 0),
          project_id: r.project.id,
          payment_stage_id: r.stage.id,
          kind: "stage",
        }));
        const invoice_amount = line_items.reduce((s, li) => s + Number(li.amount || 0), 0);
        const first = rows[0];
        const body = {
          client_id: first.project.client_id,
          project_id: first.project.id,
          payment_stage_id: first.stage.id,
          invoice_date: form.invoice_date,
          due_date: form.due_date,
          invoice_amount,
          tax_amount: Number(form.tax_amount || 0),
          currency,
          entity_type: entity,
          status: "sent",
          notes: form.notes,
          line_items,
        };
        if ((form.invoice_number || "").trim()) body.invoice_number = form.invoice_number.trim();
        await api.post("/api/invoices", body);
        toast.success(`Consolidated invoice created for ${rows.length} stages`);
      } else {
        const entity = (creating.stage.currency || "USD").toUpperCase() === "INR" ? "india" : "llc";
        const body = {
          client_id: creating.project.client_id,
          project_id: creating.project.id,
          payment_stage_id: creating.stage.id,
          invoice_date: form.invoice_date,
          due_date: form.due_date,
          invoice_amount: creating.stage.expected_amount,
          tax_amount: Number(form.tax_amount || 0),
          currency: creating.stage.currency,
          entity_type: entity,
          status: "sent",
          notes: form.notes,
        };
        if ((form.invoice_number || "").trim()) body.invoice_number = form.invoice_number.trim();
        await api.post("/api/invoices", body);
        toast.success("Invoice created — removed from pending list");
      }
      setCreating(null); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === "object" && detail.code === "outstanding_covered") {
        setBlockWarning(detail);
        return;
      }
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || "Failed to create invoice");
      toast.error(msg);
    }
  };

  const filtered = useMemo(() => {
    const pq = projectQ.trim().toLowerCase();
    const cq = clientQ.trim().toLowerCase();
    const bq = bdQ.trim().toLowerCase();
    return items.filter((it) => {
      if (pq) {
        const nm = (it.project?.project_name || "").toLowerCase();
        const code = (it.project?.project_code || "").toLowerCase();
        if (!nm.includes(pq) && !code.includes(pq)) return false;
      }
      if (cq) {
        const fn = (it.client?.firm_name || it.project?.firm_name_snapshot || "").toLowerCase();
        if (!fn.includes(cq)) return false;
      }
      if (bq) {
        const bd = (it.project?.business_development_owner_name_snapshot || "").toLowerCase();
        if (!bd.includes(bq)) return false;
      }
      return true;
    });
  }, [items, projectQ, clientQ, bdQ]);

  const resetFilters = () => { setProjectQ(""); setClientQ(""); setBdQ(""); };

  // Selected metadata — helps enforce "same client + currency" rule and drive the action bar
  const anchor = useMemo(() => {
    if (selectedIds.size === 0) return null;
    const first = items.find((it) => selectedIds.has(it.stage.id));
    return first ? { clientId: first.project.client_id, currency: (first.stage.currency || "USD").toUpperCase() } : null;
  }, [items, selectedIds]);

  const toggleSelect = (it) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(it.stage.id)) { next.delete(it.stage.id); return next; }
      // Enforce same client + currency when adding
      if (anchor) {
        const cur = (it.stage.currency || "USD").toUpperCase();
        if (it.project.client_id !== anchor.clientId || cur !== anchor.currency) {
          toast.error("Consolidation requires stages from the same client and currency.");
          return prev;
        }
      }
      next.add(it.stage.id);
      return next;
    });
  };

  const isRowDisabled = (it) => {
    if (!anchor) return false;
    if (selectedIds.has(it.stage.id)) return false;
    const cur = (it.stage.currency || "USD").toUpperCase();
    return (it.project.client_id !== anchor.clientId) || (cur !== anchor.currency);
  };

  const canConsolidate = hasRole(user, "admin", "accounts");

  const openLink = async (it) => {
    setLinking(it);
    setLinkMode("pick");
    setLinkInvoiceId("");
    setLinkInvoiceNumber("");
    setLinkInvoices([]);
    try {
      // Fetch invoices matching the project's client, then narrow to ones that
      // reference this project (primary project_id or via line_items).
      const { data } = await api.get(`/api/invoices?client_id=${it.project.client_id}`);
      const pid = it.project.id;
      const matches = (data || []).filter((inv) => {
        if (inv.project_id === pid) return true;
        return (inv.line_items || []).some((li) => li.project_id === pid);
      });
      setLinkInvoices(matches);
    } catch { /* silent — user can still use manual mode */ }
  };

  const saveLink = async () => {
    if (!linking) return;
    if (linkMode === "pick" && !linkInvoiceId) { toast.error("Pick an existing invoice to link"); return; }
    if (linkMode === "manual" && !linkInvoiceNumber.trim()) { toast.error("Enter an invoice number to log"); return; }
    setLinkBusy(true);
    try {
      const body = linkMode === "pick"
        ? { invoice_id: linkInvoiceId }
        : { invoice_number: linkInvoiceNumber.trim() };
      const { data } = await api.post(`/api/payment-stages/${linking.stage.id}/link-invoice`, body);
      toast.success(`Stage linked to ${data.invoice_number} — removed from pending`);
      setLinking(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link invoice");
    } finally { setLinkBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="pending-invoices-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight">Pending Invoices</h1>
          <p className="text-sm text-slate-500 mt-1">Every stage that hasn't been invoiced yet — undated stages (typical for original scope + fresh COs) always show; dated stages surface once their date arrives.</p>
        </div>
        {canConsolidate && (
          <div className="flex items-center gap-2 flex-wrap">
            {bulkAcceptable.length > 0 && (
              <Button
                size="sm"
                className="bg-brand hover:bg-brand-hover"
                onClick={acceptAllSuggested}
                disabled={bulkAcceptBusy}
                data-testid="accept-all-suggested-btn"
                title="Link every stage that has a highlighted match, in one click"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                {bulkAcceptBusy ? "Accepting…" : `Accept ${bulkAcceptable.length} suggested match${bulkAcceptable.length === 1 ? "" : "es"}`}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={openAudit} data-testid="open-audit-btn" title="See stages that were recently linked to invoices — un-link if wrong">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
              Audit recent links
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-none p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Project name / code</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={projectQ} onChange={(e) => setProjectQ(e.target.value)} placeholder="e.g. Aurora, TS-FW/…" className="pl-8" data-testid="pi-filter-project" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Client name</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="e.g. Beacon" className="pl-8" data-testid="pi-filter-client" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">BD name</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={bdQ} onChange={(e) => setBdQ(e.target.value)} placeholder="e.g. Sarah" className="pl-8" data-testid="pi-filter-bd" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={resetFilters} data-testid="pi-reset-filters">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
            </Button>
            <div className="text-xs text-slate-500">
              {filtered.length} of {items.length}
            </div>
          </div>
        </div>
      </Card>

      {/* Consolidation action bar */}
      {canConsolidate && selectedIds.size > 0 && (
        <div className="sticky top-0 z-20 bg-brand-tint border border-brand/30 rounded-lg px-4 py-3 flex items-center justify-between gap-3" data-testid="consolidate-bar">
          <div className="text-sm text-slate-700">
            <b>{selectedIds.size}</b> stage{selectedIds.size === 1 ? "" : "s"} selected
            {anchor && <span className="text-slate-500"> · same client & {anchor.currency}</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="consolidate-clear">Clear</Button>
            <Button
              size="sm"
              className="bg-brand hover:bg-brand-hover"
              onClick={openConsolidated}
              disabled={selectedIds.size < 2}
              data-testid="consolidate-create-btn"
            >
              <Layers className="w-3.5 h-3.5 mr-1.5" />
              Create consolidated invoice
            </Button>
          </div>
        </div>
      )}

      <Card className="border-slate-200 shadow-none p-4">
        <div className="overflow-x-auto">
        <PendingInvoicesTable
          rows={filtered}
          user={user}
          navigate={navigate}
          openCreate={openCreate}
          openLink={openLink}
          acceptSuggestion={acceptSuggestion}
          suggestBusyStageId={suggestBusyStageId}
          canConsolidate={canConsolidate}
          selectedIds={selectedIds}
          toggleSelect={toggleSelect}
          isRowDisabled={isRowDisabled}
        />
        </div>
      </Card>

      <Dialog open={!!creating} onOpenChange={(v) => !v && setCreating(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {creating?.consolidated
                ? `Create Consolidated Invoice · ${creating.rows.length} stages`
                : `Create Invoice · ${creating?.project?.project_code}`}
            </DialogTitle>
          </DialogHeader>
          {creating && !creating.consolidated && (
            <div className="space-y-3">
              <div className="text-sm text-slate-500">
                {creating.client?.firm_name} — Stage {creating.stage.stage_number}: <span className="font-medium text-slate-900">{creating.stage.stage_name}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label>Invoice Number <span className="text-xs text-slate-400 font-normal">· auto-filled from series, editable</span></Label>
                  <Input data-testid="pending-invoice-number" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="Fetching next number…" />
                </div>
                <div className="space-y-1.5">
                  <Label>Invoice Date</Label>
                  <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Due Date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Amount</Label>
                  <Input value={formatCurrency(creating.stage.expected_amount, creating.stage.currency)} readOnly className="bg-slate-50" />
                </div>
                <div className="space-y-1.5">
                  <Label>Tax</Label>
                  <Input type="number" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                </div>
              </div>
            </div>
          )}
          {creating?.consolidated && (
            <div className="space-y-3">
              <div className="text-sm text-slate-500">
                <b>{creating.client?.firm_name}</b> · {creating.currency} · one invoice will cover every stage below and each project's payment status will update accordingly.
              </div>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-56 overflow-y-auto">
                {creating.rows.map((r) => (
                  <div key={r.stage.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`consolidate-line-${r.stage.id}`}>
                    <div className="min-w-0 pr-3">
                      <div className="truncate">
                        <span className="font-mono text-xs text-slate-500 mr-2">{r.project.project_code}</span>
                        {r.project.project_name}
                      </div>
                      <div className="text-xs text-slate-500 truncate">Stage {r.stage.stage_number}: {r.stage.stage_name}</div>
                    </div>
                    <div className="tabular-nums font-medium text-slate-800">{formatCurrency(r.stage.expected_amount, r.stage.currency)}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end text-sm">
                <span className="text-slate-500 mr-2">Subtotal:</span>
                <span className="font-heading font-semibold tabular-nums">
                  {formatCurrency(creating.rows.reduce((s, r) => s + Number(r.stage.expected_amount || 0), 0), creating.currency)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label>Invoice Number <span className="text-xs text-slate-400 font-normal">· auto-filled from series, editable</span></Label>
                  <Input data-testid="consolidated-invoice-number" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="Fetching next number…" />
                </div>
                <div className="space-y-1.5">
                  <Label>Invoice Date</Label>
                  <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Due Date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Tax</Label>
                  <Input type="number" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(null)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={saveInvoice} data-testid="save-pending-invoice">
              {creating?.consolidated ? "Create Consolidated Invoice" : "Create Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {blockWarning && (
        <Dialog open={!!blockWarning} onOpenChange={(v) => !v && setBlockWarning(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pi-over-invoice-dialog">
            <DialogHeader>
              <DialogTitle className="text-amber-800">Over-invoicing blocked</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">{blockWarning.message}</p>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3">
                {(blockWarning.projects || []).map((p) => {
                  const headroom = Number(p.remaining_headroom ?? Math.max((p.project_value || 0) - (p.already_invoiced || 0), 0));
                  const overBy = Math.max(Number(p.new_amount || 0) - headroom, 0);
                  return (
                    <div key={p.project_id} className="text-xs" data-testid={`pi-block-project-${p.project_id}`}>
                      <div className="font-mono font-semibold text-amber-900">
                        {p.project_code || "—"} {p.project_name ? `· ${p.project_name}` : ""}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-slate-700">
                        <div>Project value: <span className="font-medium">{p.currency || ""} {Number(p.project_value).toLocaleString()}</span></div>
                        <div>Already invoiced: <span className="font-medium">{p.currency || ""} {Number(p.already_invoiced).toLocaleString()}</span></div>
                        <div>This invoice adds: <span className="font-medium">{p.currency || ""} {Number(p.new_amount).toLocaleString()}</span></div>
                        <div>
                          <span className={headroom <= 0 ? "text-rose-700" : "text-emerald-700"}>Remaining headroom:</span>{" "}
                          <span className={`font-semibold ${headroom <= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                            {p.currency || ""} {headroom.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      {overBy > 0 && (
                        <div className="mt-1 text-rose-700 font-medium">
                          Would over-bill by {p.currency || ""} {overBy.toLocaleString()} — remove this project from the selection or reduce its stage amount.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">
                Uncheck the offending stage from the pending list and re-run "Create consolidated invoice", or edit the stage's expected amount from the project page.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBlockWarning(null)} data-testid="pi-block-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ─── Link Existing Invoice dialog ─────────────────────────────────
          Retroactively attach a stage to an invoice created via the
          Invoices page so it disappears from Pending Invoices. Two modes:
          • "Pick" — dropdown of invoices already issued for this project
          • "Manual" — just log an invoice number (light link)          */}
      <Dialog open={!!linking} onOpenChange={(v) => { if (!v && !linkBusy) setLinking(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pi-link-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Link existing invoice</DialogTitle>
          </DialogHeader>
          {linking && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                <div className="text-xs text-slate-500">
                  {linking.client?.firm_name || linking.project?.firm_name_snapshot} · <span className="font-mono">{linking.project?.project_code}</span>
                </div>
                <div className="font-medium text-slate-800 mt-0.5">
                  Stage {linking.stage.stage_number}: {linking.stage.stage_name}
                  <span className="ml-2 text-slate-500">{formatCurrency(linking.stage.expected_amount, linking.stage.currency)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                <button
                  type="button"
                  onClick={() => setLinkMode("pick")}
                  className={`px-2.5 py-1 rounded text-xs font-medium ${linkMode === "pick" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid="link-mode-pick"
                >
                  Pick existing invoice
                </button>
                <button
                  type="button"
                  onClick={() => setLinkMode("manual")}
                  className={`px-2.5 py-1 rounded text-xs font-medium ${linkMode === "manual" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid="link-mode-manual"
                >
                  Just log a number
                </button>
              </div>

              {linkMode === "pick" ? (
                <div className="space-y-2">
                  <Label className="text-xs uppercase text-slate-500">Invoice for this project</Label>
                  {linkInvoices.length === 0 ? (
                    <div className="rounded border border-dashed border-slate-200 text-slate-500 text-xs px-3 py-4 text-center">
                      No invoices found for this project yet. Switch to "Just log a number" or create the invoice first.
                    </div>
                  ) : (
                    <Select value={linkInvoiceId} onValueChange={setLinkInvoiceId}>
                      <SelectTrigger data-testid="link-invoice-select">
                        <SelectValue placeholder="Choose an invoice…" />
                      </SelectTrigger>
                      <SelectContent>
                        {linkInvoices.map((inv) => (
                          <SelectItem key={inv.id} value={inv.id}>
                            {inv.invoice_number} · {formatCurrency(inv.invoice_amount, inv.currency)} · {inv.invoice_date?.slice(0, 10)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-[11px] text-slate-500">
                    We'll mark this stage as invoiced against the chosen invoice, refresh the project totals and remove the row from Pending Invoices.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs uppercase text-slate-500">Invoice number</Label>
                  <Input
                    value={linkInvoiceNumber}
                    onChange={(e) => setLinkInvoiceNumber(e.target.value)}
                    placeholder="e.g. TS/26-27/SEP/077"
                    data-testid="link-invoice-number"
                    className="font-mono"
                  />
                  <p className="text-[11px] text-slate-500">
                    Just logs the number on the stage so Pending Invoices stops showing it. No accounting side-effect — use this for legacy invoices raised outside the system.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinking(null)} disabled={linkBusy}>Cancel</Button>
            <Button
              className="bg-brand hover:bg-brand-hover"
              onClick={saveLink}
              disabled={linkBusy || (linkMode === "pick" && !linkInvoiceId) || (linkMode === "manual" && !linkInvoiceNumber.trim())}
              data-testid="link-invoice-save"
            >
              {linkBusy ? "Linking…" : "Link invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ─── Audit / Undo dialog ─────────────────────────────────────────
          Lists stages that were recently linked to invoices so an admin can
          un-link a wrong pick in one click.                                */}
      <Dialog open={auditOpen} onOpenChange={(v) => { if (!v) setAuditOpen(false); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pi-audit-dialog">
          <DialogHeader>
            <DialogTitle>Recently linked stages</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
            <Label className="text-xs uppercase text-slate-500">Window</Label>
            <Select value={String(auditWindow)} onValueChange={(v) => { setAuditWindow(Number(v)); }}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="audit-window-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadAudit} data-testid="audit-reload">
              <RotateIcon className="w-3 h-3 mr-1" /> Reload
            </Button>
            <div className="ml-auto text-xs text-slate-500">{auditEvents.length} event{auditEvents.length === 1 ? "" : "s"}</div>
          </div>
          {auditEvents.length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-6">
              No stages linked in this window. Recent links will show up here for one-click un-linking.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100 mt-2">
              {auditEvents.map((ev) => (
                <div key={ev.stage.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`audit-row-${ev.stage.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate">
                      <span className="font-mono text-xs text-slate-500 mr-2">{ev.project?.project_code || "—"}</span>
                      <span className="font-medium">{ev.project?.project_name}</span>
                      <span className="text-slate-500"> · Stage {ev.stage.stage_number}: {ev.stage.stage_name}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3">
                      <span>Linked to <span className="font-mono">{ev.invoice_number}</span></span>
                      <span>· {ev.linked_via === "invoice_id" ? "picked" : "manual"}</span>
                      {ev.actor && <span>· by {ev.actor}</span>}
                      {ev.linked_at && <span>· {new Date(ev.linked_at).toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="tabular-nums text-slate-600 whitespace-nowrap">{formatCurrency(ev.stage.expected_amount, ev.stage.currency)}</div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-rose-600 border-rose-200 hover:bg-rose-50"
                    disabled={auditBusyId === ev.stage.id}
                    onClick={() => unlinkStage(ev)}
                    data-testid={`unlink-stage-${ev.stage.id}`}
                    title="Un-link and return to pending"
                  >
                    <Link2 className="w-3 h-3 mr-1" />
                    {auditBusyId === ev.stage.id ? "…" : "Un-link"}
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PendingInvoicesTable({ rows, user, navigate, openCreate, openLink, acceptSuggestion, suggestBusyStageId, canConsolidate, selectedIds, toggleSelect, isRowDisabled }) {
  const columns = [
    { key: "client", label: "Client", getValue: (r) => r.client?.firm_name || r.project?.firm_name_snapshot },
    { key: "project", label: "Project", getValue: (r) => r.project?.project_name },
    { key: "bd", label: "BD", getValue: (r) => r.project?.business_development_owner_name_snapshot },
    { key: "stage", label: "Stage", getValue: (r) => r.stage?.stage_name },
    { key: "amount", label: "Amount", numeric: true, align: "right", getValue: (r) => r.stage?.expected_amount },
    { key: "trigger", label: "Trigger", getValue: (r) => r.stage?.trigger },
    { key: "invoice_date", label: "Invoice Date", getValue: (r) => r.stage?.expected_invoice_date },
    { key: "days_pending", label: "Days Pending", numeric: true, align: "right", getValue: (r) => r.days_pending },
    { key: "status", label: "Status", getValue: (r) => r.stage?.status },
  ];
  const { sortedRows, header } = useTableSort(rows, columns, "invoice_date", "asc");
  return (
    <table className="data-table w-full">
      <thead>
        <tr>
          {canConsolidate && <th className="w-8"></th>}
          {header.props.children}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((it, i) => {
          const disabled = isRowDisabled ? isRowDisabled(it) : false;
          const checked = selectedIds ? selectedIds.has(it.stage?.id) : false;
          return (
            <tr key={it.stage?.id || `${it.project?.id}-${i}`} className={disabled ? "opacity-40" : ""}>
              {canConsolidate && (
                <td className="w-8">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleSelect(it)}
                    data-testid={`select-stage-${it.stage.id}`}
                    title={disabled ? "Only stages from the same client and currency can be consolidated" : "Include in consolidated invoice"}
                  />
                </td>
              )}
              <td className="font-medium">{it.client?.firm_name || it.project?.firm_name_snapshot || "—"}</td>
              <td>{it.project?.project_name}</td>
              <td className="text-slate-600 text-sm">{it.project?.business_development_owner_name_snapshot || "—"}</td>
              <td>{it.stage.stage_name}</td>
              <td className="text-right">
                <div className="tabular-nums">{formatCurrency(it.stage.expected_amount, it.stage.currency)}</div>
                {Array.isArray(it.suggested_invoices) && it.suggested_invoices.length > 0 && acceptSuggestion && (
                  <div className="flex flex-wrap items-center gap-1 mt-1 justify-end" data-testid={`suggest-wrap-${it.stage.id}`}>
                    {it.suggested_invoices.map((inv) => (
                      <button
                        key={inv.id}
                        type="button"
                        disabled={suggestBusyStageId === it.stage.id}
                        onClick={() => acceptSuggestion(it.stage, inv)}
                        className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-brand/30 bg-brand-tint text-brand-dark hover:bg-brand hover:text-white text-[11px] font-medium transition disabled:opacity-60"
                        title={`One-click accept — link this stage to ${inv.invoice_number}`}
                        data-testid={`accept-suggest-${it.stage.id}-${inv.id}`}
                      >
                        <Sparkles className="w-3 h-3" />
                        Match {inv.invoice_number}
                      </button>
                    ))}
                  </div>
                )}
              </td>
              <td className="text-slate-500 text-xs">{it.stage.trigger || "—"}</td>
              <td className="font-medium text-orange-700">{it.stage.expected_invoice_date?.slice(0, 10) || "—"}</td>
              <td className={`text-right ${it.days_pending > 0 ? "text-orange-600 font-medium" : ""}`}>{it.days_pending ?? "—"}</td>
              <td><StatusBadge status={it.stage.status} /></td>
              <td>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/projects/${it.project.id}`)} data-testid={`view-project-${it.project.id}`}>View</Button>
                  {hasRole(user, "admin", "accounts") && openLink && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openLink(it)}
                      data-testid={`link-invoice-${it.stage.id}`}
                      title="Link an already-created invoice to this stage"
                    >
                      <Link2 className="w-3 h-3 mr-1" />Link
                    </Button>
                  )}
                  {hasRole(user, "admin", "accounts") && (
                    <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => openCreate(it)} data-testid={`create-invoice-${it.stage.id}`}>
                      <FileText className="w-3 h-3 mr-1" />Create Invoice
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
        {sortedRows.length === 0 && (
          <tr><td colSpan={canConsolidate ? 11 : 10} className="text-center text-slate-500 py-8">No pending invoices — every due milestone is already invoiced.</td></tr>
        )}
      </tbody>
    </table>
  );
}
